<template>
  <v-container fluid grid-list-md pa-3>
    <!-- Loading Dialog -->
    <v-dialog
      v-model="isLoading"
      persistent
      fullscreen
      transition="fade-transition"
    >
      <v-layout justify-center align-center fill-height style="background-color: #1a19199c">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-layout>
    </v-dialog>

    <!-- File Upload Section -->
    <v-card class="mb-4">
      <v-card-title class="primary white--text">
        <v-icon class="mr-2 white--text">upload</v-icon>
        นำเข้าข้อมูลจากไฟล์ XLS/XLSX
      </v-card-title>
      <v-card-text class="pt-4">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6>
            <v-text-field
              id="fileInput"
              label="เลือกไฟล์ XLS/XLSX"
              v-model="fileName"
              prepend-icon="attach_file"
              @click="onPickFile"
              readonly
            ></v-text-field>
            <!-- Hidden file input -->
            <input
              type="file"
              style="display: none"
              ref="fileInput"
              accept=".xls,.xlsx"
              @change="onFilePicked"
            >
          </v-flex>
          <v-flex xs12 sm6 class="text-right">
            <v-btn
              color="primary"
              :loading="isLoading"
              :disabled="!selectedFile"
              @click="uploadFile"
            >
              <v-icon class="mr-2">cloud_upload</v-icon>
              อัปโหลดและแสดงตัวอย่าง
            </v-btn>
          </v-flex>
        </v-layout>
        <v-layout row wrap class="mt-0 ml-1 mb-3">
          <v-flex xs12>
            <span class="caption grey--text">
              สามารถดาวน์โหลดไฟล์ตัวอย่างได้ที่:
              <a :href="templateFile" download="plant-100m-import-template.xlsx">
                plant-100m-import-template.xlsx
              </a>
            </span>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Preview Data Section -->
    <div v-if="parsedData.users.length > 0 || parsedData.user_plants.length > 0">
      <!-- Users Table -->
      <v-card class="mb-4">
        <v-card-title class="amber lighten-1">
          <v-icon class="mr-2">people</v-icon>
          ตารางผู้ใช้ ({{ parsedData.users.length }} รายการ)
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="userHeaders"
            :items="parsedData.users"
            :pagination.sync="userPagination"
            :rows-per-page-items="rowsPerPageOptions"
            class="elevation-1"
            item-key="_localId"
          >
            <template slot="items" slot-scope="props">
              <tr :class="{'red lighten-4': !checkUserValid(props.item)}">
                <!-- เลขบัตรประชาชน -->
                <td>{{ props.item.card_id }}</td>

                <!-- คำนำหน้า -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_title_' + props.item._localId"
                    v-model="props.item.title"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.title }}</td>

                <!-- ชื่อ -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_name_' + props.item._localId"
                    v-model="props.item.name"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.name }}</td>

                <!-- นามสกุล -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_surname_' + props.item._localId"
                    v-model="props.item.surname"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.surname }}</td>

                <!-- ชื่อบริษัท/หน่วยงาน -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_companyname_' + props.item._localId"
                    v-model="props.item.companyname"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.companyname }}</td>

                <!-- เบอร์โทรศัพท์ -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_phone_' + props.item._localId"
                    v-model="props.item.phone"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.phone }}</td>

                <!-- ที่อยู่ -->
                <td v-if="editingUserId === props.item._localId">
                  <v-text-field
                    :id="'user_address_' + props.item._localId"
                    v-model="props.item.address"
                    dense
                    single-line
                  ></v-text-field>
                </td>
                <td v-else>{{ props.item.address }}</td>

                <!-- การกระทำ -->
                <td class="text-center">
                  <v-layout row align-center justify-center>
                    <!-- ลบผู้ใช้ -->
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="action-btn"
                        @click="deleteUser(props.item)"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                      <span>ลบผู้ใช้</span>
                    </v-tooltip>

                    <!-- แก้ไขผู้ใช้ -->
                    <v-tooltip bottom v-if="editingUserId !== props.item._localId">
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="action-btn"
                        @click="startEditingUser(props.item)"
                      >
                        <v-icon>edit</v-icon>
                      </v-btn>
                      <span>แก้ไขผู้ใช้</span>
                    </v-tooltip>

                    <!-- ปุ่มบันทึก/ยกเลิก เมื่ออยู่ในโหมดแก้ไข -->
                    <template v-if="editingUserId === props.item._localId">
                      <v-tooltip bottom>
                        <v-btn
                          slot="activator"
                          icon
                          small
                          class="action-btn"
                          @click="saveUserEdit"
                        >
                          <v-icon>check</v-icon>
                        </v-btn>
                        <span>บันทึก</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <v-btn
                          slot="activator"
                          icon
                          small
                          class="action-btn"
                          @click="cancelUserEdit"
                        >
                          <v-icon>close</v-icon>
                        </v-btn>
                        <span>ยกเลิก</span>
                      </v-tooltip>
                    </template>
                  </v-layout>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- User Plants Table -->
      <v-card class="mb-4">
        <v-card-title class="green lighten-2">
          <v-icon class="mr-2">nature</v-icon>
          ตารางบันทึกการปลูกต้นไม้ ({{ parsedData.user_plants.length }} รายการ)
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="plantHeaders"
            :items="parsedData.user_plants"
            :pagination.sync="plantPagination"
            :rows-per-page-items="rowsPerPageOptions"
            class="elevation-1"
            item-key="_localId"
          >
            <template slot="items" slot-scope="props">
              <tr :class="{'red lighten-4': !checkPlantValid(props.item)}">
                <!-- เลขบัตรประชาชน -->
                <td>{{ props.item.card_id }}</td>

                <!-- ชนิดไม้ -->
                <td>{{ props.item.plant_name || '(' + props.item.plant_id + ')' }}</td>

                <!-- จำนวน -->
                <td class="text-center">{{ props.item.amount }}</td>

                <!-- วันที่ปลูก -->
                <td>{{ formatDateDisplay(props.item.date_planting) }}</td>

                <!-- ตำบล -->
                <td>{{ props.item.tambon_name }}</td>

                <!-- อำเภอ -->
                <td>{{ props.item.amphoe_name }}</td>

                <!-- จังหวัด -->
                <td>{{ props.item.province_name }}</td>

                <!-- ได้รับต้นไม้จาก -->
                <td>{{ props.item.sub_department_name_level2 }}</td>

                <!-- การกระทำ -->
                <td class="text-center">
                  <v-layout row align-center justify-center>
                    <!-- ลบบันทึกการปลูก -->
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="action-btn"
                        @click="deletePlant(props.item)"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                      <span>ลบบันทึกการปลูก</span>
                    </v-tooltip>

                    <!-- แก้ไข: เปิด dialog -->
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="action-btn"
                        @click="openPlantEditDialog(props.item)"
                      >
                        <v-icon>edit</v-icon>
                      </v-btn>
                      <span>แก้ไขบันทึก</span>
                    </v-tooltip>
                  </v-layout>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Import Button -->
      <v-layout column align-end class="mb-4">
        <div v-if="hasInvalidData" class="text-right mb-2">
          <span class="red--text font-weight-bold">
            ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีรายการที่ข้อมูลไม่ครบถ้วน
          </span><br>
          <span class="red--text font-weight-bold" v-if="invalidUserCount > 0">
            ตารางผู้ใช้: {{ invalidUserCount }} รายการที่ยังกรอกข้อมูลไม่ครบ
          </span><br v-if="invalidUserCount > 0 && invalidPlantCount > 0">
          <span class="red--text font-weight-bold" v-if="invalidPlantCount > 0">
            ตารางบันทึกการปลูกต้นไม้: {{ invalidPlantCount }} รายการที่ยังกรอกข้อมูลไม่ครบ
          </span>
        </div>
        <v-btn
          color="success"
          large
          @click="confirmImport"
          :loading="isImporting"
          :disabled="hasInvalidData"
        >
          <v-icon class="mr-2">check_circle</v-icon>
          นำเข้าข้อมูล
        </v-btn>
      </v-layout>
    </div>

    <!-- Empty State -->
    <v-card v-else class="mb-4">
      <v-card-text class="text-center pa-5">
        <p class="grey--text">โปรดเลือกไฟล์ XLS/XLSX เพื่อแสดงตัวอย่างข้อมูล</p>
      </v-card-text>
    </v-card>

    <!-- Plant Edit Dialog -->
    <v-dialog v-model="plantEditDialog" max-width="800px">
      <v-card>
        <v-card-title class="green lighten-2 white--text">
          <v-icon class="mr-2 white--text">edit</v-icon>
          แก้ไขบันทึกการปลูกต้นไม้
        </v-card-title>

        <v-card-text>
          <v-form v-if="plantEditModel" ref="plantEditForm">
            <v-layout row wrap>
              <!-- เลขบัตร -->
              <v-flex xs12 sm6>
                <v-text-field
                  label="เลขบัตรประชาชน"
                  v-model="plantEditModel.card_id"
                  dense
                  readonly
                ></v-text-field>
              </v-flex>

              <!-- วันที่ปลูก -->
              <v-flex xs12 sm6>
                <v-menu
                  v-model="plantEditDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      label="วันที่ปลูก"
                      dense
                      readonly
                      prepend-inner-icon="event"
                      v-on="on"
                      :value="plantEditDateDisplay"
                      placeholder="DD/MM/YYYY"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="plantEditModel.date_planting"
                    locale="th"
                    no-title
                    @input="plantEditDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>

              <!-- ชนิดไม้ -->
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="plantEditModel.plant_name"
                  :items="plantOptions"
                  :item-text="'name'"
                  :item-value="'name'"
                  :loading="plantLoading"
                  :search-input.sync="plantSearch"
                  label="ชนิดไม้"
                  hide-no-data
                  hide-selected
                  clearable
                  dense
                  @update:search-input="onSearchPlant"
                  @change="onSelectPlant"
                ></v-autocomplete>
              </v-flex>

              <!-- จำนวน -->
              <v-flex xs12 sm6>
                <v-text-field
                  label="จำนวน"
                  v-model.number="plantEditModel.amount"
                  type="number"
                  dense
                ></v-text-field>
              </v-flex>

              <!-- จังหวัด -->
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="plantEditModel.province_name"
                  :items="provinceOptions"
                  :item-text="'name'"
                  :item-value="'name'"
                  :loading="provinceLoading"
                  :search-input.sync="provinceSearch"
                  label="จังหวัด"
                  hide-no-data
                  hide-selected
                  clearable
                  dense
                  @update:search-input="onSearchProvince"
                  @change="onSelectProvince"
                ></v-autocomplete>
              </v-flex>

              <!-- อำเภอ -->
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="plantEditModel.amphoe_name"
                  :items="amphoeOptions"
                  :item-text="'name'"
                  :item-value="'name'"
                  :loading="amphoeLoading"
                  :search-input.sync="amphoeSearch"
                  label="อำเภอ"
                  hide-no-data
                  hide-selected
                  clearable
                  dense
                  @update:search-input="onSearchAmphoe"
                  @change="onSelectAmphoe"
                ></v-autocomplete>
              </v-flex>

              <!-- ตำบล -->
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="plantEditModel.tambon_name"
                  :items="tambonOptions"
                  :item-text="'name'"
                  :item-value="'name'"
                  :loading="tambonLoading"
                  :search-input.sync="tambonSearch"
                  label="ตำบล"
                  hide-no-data
                  hide-selected
                  clearable
                  dense
                  @update:search-input="onSearchTambon"
                  @change="onSelectTambon"
                ></v-autocomplete>
              </v-flex>

              <!-- ได้รับต้นไม้จาก -->
              <v-flex xs12>
                <v-autocomplete
                  v-model="plantEditModel.sub_department_name_level2"
                  :items="subDepartmentOptions"
                  :item-text="'name'"
                  :item-value="'name'"
                  :loading="subDepartmentLoading"
                  :search-input.sync="subDepartmentSearch"
                  label="ได้รับต้นไม้จาก"
                  hide-no-data
                  hide-selected
                  clearable
                  dense
                  @update:search-input="onSearchSubDepartment"
                  @change="onSelectSubDepartment"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closePlantEditDialog">ยกเลิก</v-btn>
          <v-btn color="green darken-1" class="white--text" @click="savePlantDialog">
            <v-icon left>check</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Plants Dialog -->
    <v-dialog v-model="missingPlantsDialog" persistent max-width="500px">
      <v-card>
        <v-card-title class="headline amber lighten-1">
          <v-icon class="mr-2">warning</v-icon>
          เพิ่มชนิดไม้ใหม่
        </v-card-title>

        <v-card-text>
          <p>พบชนิดไม้ที่ไม่มีในระบบ เลือกชนิดไม้ที่ต้องการเพิ่ม:</p>
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <a href="javascript:void(0)" @click="selectAllMissing">เลือกทั้งหมด</a> |
              <a href="javascript:void(0)" @click="selectNoneMissing">ไม่เลือกเลย</a>
            </v-flex>
          </v-layout>

          <div style="max-height: 300px; overflow-y: auto;">
            <v-checkbox
              v-for="(plant, index) in missingPlantsList"
              :key="index"
              v-model="missingPlantsSelected"
              :label="plant"
              :value="plant"
              hide-details
              class="mt-0"
            ></v-checkbox>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeMissingPlantsDialog">ยกเลิก</v-btn>
          <v-btn color="primary" @click="confirmAddMissingPlants">
            <v-icon left>add_circle</v-icon>
            เพิ่มชนิดไม้
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
export default {
  name: 'ComponentAdminImportNurseryXLS',
  data() {
    return {
      templateFile: '/templates/plant-100m-import-template.xlsx',
      fileName: '',
      selectedFile: null,
      isLoading: false,
      isImporting: false,
      userLocalIdCounter: 1,
      plantLocalIdCounter: 1,
      editingUserId: null,
      editingUserBackup: null,

      parsedData: {
        users: [],
        user_plants: []
      },

      userHeaders: [
        { text: 'เลขบัตรประชาชน', value: 'card_id', align: 'left' },
        { text: 'คำนำหน้า', value: 'title', align: 'left' },
        { text: 'ชื่อ', value: 'name', align: 'left' },
        { text: 'นามสกุล', value: 'surname', align: 'left' },
        { text: 'ชื่อบริษัท/หน่วยงาน', value: 'companyname', align: 'left' },
        { text: 'เบอร์โทรศัพท์', value: 'phone', align: 'left' },
        { text: 'ที่อยู่', value: 'address', align: 'left' },
        { text: 'การกระทำ', value: 'actions', align: 'center', sortable: false, width: '160px' },
      ],

      plantHeaders: [
        { text: 'เลขบัตรประชาชน', value: 'card_id', align: 'left' },
        { text: 'ชนิดไม้', value: 'plant_name', align: 'left' },
        { text: 'จำนวน', value: 'amount', align: 'center' },
        { text: 'วันที่ปลูก', value: 'date_planting', align: 'left' },
        { text: 'ตำบล', value: 'tambon_name', align: 'left' },
        { text: 'อำเภอ', value: 'amphoe_name', align: 'left' },
        { text: 'จังหวัด', value: 'province_name', align: 'left' },
        { text: 'ได้รับต้นไม้จาก', value: 'sub_department_name_level2', align: 'left' },
        { text: 'การกระทำ', value: 'actions', align: 'center', sortable: false, width: '160px' },
      ],

      // dialog สำหรับแก้ไข user_plants
      plantEditDialog: false,
      plantEditModel: null,   // object ที่ใช้แก้ใน dialog
      plantEditTargetId: null, // _localId ของแถวจริง

      // autocomplete state + cache
      plantSearch: '',
      plantOptions: [],
      plantLoading: false,
      plantCache: new Map(),

      provinceSearch: '',
      provinceOptions: [],
      provinceLoading: false,
      provinceCache: new Map(),

      amphoeSearch: '',
      amphoeOptions: [],
      amphoeLoading: false,
      amphoeCache: new Map(),

      tambonSearch: '',
      tambonOptions: [],
      tambonLoading: false,
      tambonCache: new Map(),

      subDepartmentSearch: '',
      subDepartmentOptions: [],
      subDepartmentLoading: false,
      subDepartmentCache: new Map(),

      plantEditDateMenu: false,
      rowsPerPageOptions: [10, 20, 50, 100],
      userPagination: {
        sortBy: null,
        descending: false,
        page: 1,
        rowsPerPage: 10
      },
      plantPagination: {
        sortBy: null,
        descending: false,
        page: 1,
        rowsPerPage: 10
      },
      plantPagination: {
        sortBy: null,
        descending: false,
        page: 1,
        rowsPerPage: 10
      },

      // Missing plants handling
      missingPlantsDialog: false,
      missingPlantsList: [],
      missingPlantsSelected: []
    };
  },
  computed: {
    plantEditDateDisplay: {
      get() {
        if (!this.plantEditModel || !this.plantEditModel.date_planting) return '';
        return this.formatDateDisplay(this.plantEditModel.date_planting);
      },
      set(val) {
        if (!this.plantEditModel) return;
        this.$set(this.plantEditModel, 'date_planting', this.parseDisplayDate(val));
      }
    },
    invalidUserCount() {
      return this.parsedData.users.filter(u => !this.checkUserValid(u)).length;
    },
    invalidPlantCount() {
      return this.parsedData.user_plants.filter(p => !this.checkPlantValid(p)).length;
    },
    hasInvalidData() {
      return this.invalidUserCount > 0 || this.invalidPlantCount > 0;
    }
  },
  methods: {
    checkUserValid(user) {
      if (!user) return false;
      return !!user.card_id;
    },
    checkPlantValid(plant) {
      if (!plant) return false;
      return (
        !!plant.card_id &&
        !!plant.date_planting &&
        !!plant.plant_name &&
        !!plant.amount &&
        !!plant.province_name &&
        !!plant.amphoe_name &&
        !!plant.tambon_name
      );
    },

    formatDateDisplay(dateStr) {
      if (!dateStr) return '';
      const value = String(dateStr).trim();
      if (!value) return '';

      const isoParts = value.split('-');
      if (isoParts.length === 3 && isoParts[0].length === 4) {
        const [year, month, day] = isoParts;
        if (year && month && day) {
          const yearNum = parseInt(year, 10);
          const displayYear = Number.isNaN(yearNum)
            ? year
            : String(yearNum > 2400 ? yearNum : yearNum + 543);
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${displayYear}`;
        }
      }

      const slashParts = value.split('/');
      if (slashParts.length === 3) {
        const [first, second, year] = slashParts.map(part => part.trim());
        const firstNum = parseInt(first, 10);
        const yearNum = parseInt(year, 10);
        const displayYear = Number.isNaN(yearNum)
          ? year
          : String(yearNum > 2400 ? yearNum : yearNum + 543);

        if (!Number.isNaN(firstNum) && firstNum > 12) {
          return `${first.padStart(2, '0')}/${second.padStart(2, '0')}/${displayYear}`;
        }
        return `${second.padStart(2, '0')}/${first.padStart(2, '0')}/${displayYear}`;
      }

      return value;
    },

    parseDisplayDate(val) {
      const raw = (val || '').trim();
      if (!raw) return '';
      const slashParts = raw.split('/');
      if (slashParts.length === 3) {
        const [day, month, year] = slashParts.map(part => part.trim());
        const dayNum = parseInt(day, 10);
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);
        if (
          !Number.isNaN(dayNum) &&
          !Number.isNaN(monthNum) &&
          !Number.isNaN(yearNum) &&
          monthNum >= 1 &&
          monthNum <= 12 &&
          dayNum >= 1 &&
          dayNum <= 31
        ) {
          const yearAd = yearNum > 2400 ? yearNum - 543 : yearNum;
          return `${String(yearAd).padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }

      const isoParts = raw.split('-');
      if (isoParts.length === 3 && isoParts[0].length === 4) {
        const yearNum = parseInt(isoParts[0], 10);
        const yearAd = !Number.isNaN(yearNum) && yearNum > 2400 ? yearNum - 543 : yearNum;
        return `${String(yearAd).padStart(4, '0')}-${isoParts[1].padStart(2, '0')}-${isoParts[2].padStart(2, '0')}`;
      }

      return raw;
    },

    onPickFile() {
      this.$refs.fileInput.click();
    },

    onFilePicked(event) {
      const files = event.target.files;
      if (files[0] !== undefined) {
        this.fileName = files[0].name;
        // Check validity of file
        if (this.fileName.lastIndexOf('.') <= 0) {
          this.fileName = '';
          this.selectedFile = null;
          return;
        }
        // If valid, store the file
        this.selectedFile = files[0];
      } else {
        this.fileName = '';
        this.selectedFile = null;
      }
    },

    async uploadFile() {
      if (!this.selectedFile) {
        this.$swal({
          type: 'warning',
          title: 'ข้อความแจ้งเตือน',
          text: 'โปรดเลือกไฟล์ก่อน'
        });
        return;
      }

      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const response = await this.axios.post(
          `${process.env.VUE_APP_IPFLASK}/parseNurseryXLS`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true
          }
        );

        if (response.data && response.data.users && response.data.user_plants) {
          const users = (response.data.users || []).map(u => ({
            ...u,
            _localId: 'user_' + (this.userLocalIdCounter++)
          }));

          const user_plants = (response.data.user_plants || []).map(p => ({
            ...p,
            _localId: 'plant_' + (this.plantLocalIdCounter++)
          }));

          this.parsedData = { users, user_plants };
        } else if (response.data && response.data.missing_plants) {
          // Case: Missing plants found
          this.missingPlantsList = response.data.missing_plants;
          this.missingPlantsSelected = [...this.missingPlantsList]; // Default select all

          this.$swal({
            type: 'warning',
            title: 'แสดงตัวอย่างล้มเหลว',
            text: `พบชนิดไม้ที่ไม่มีในฐานข้อมูล ${this.missingPlantsList.length} ชนิด`,
            confirmButtonText: 'เพิ่มชนิดไม้',
            showCancelButton: true,
            cancelButtonText: 'ปิด'
          }).then((result) => {
            if (result.value) {
              this.missingPlantsDialog = true;
            }
          });
        } else {
          throw new Error('รูปแบบข้อมูลไม่ถูกต้อง');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        const errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'ไม่สามารถอัปโหลดไฟล์ได้';
        this.$swal({
          type: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: errorMessage
        });
      } finally {
        this.isLoading = false;
      }
    },

    /* ---- MISSING PLANTS HANDLER ---- */
    selectAllMissing() {
      this.missingPlantsSelected = [...this.missingPlantsList];
    },
    selectNoneMissing() {
      this.missingPlantsSelected = [];
    },
    closeMissingPlantsDialog() {
      this.missingPlantsDialog = false;
      this.missingPlantsList = [];
      this.missingPlantsSelected = [];
    },
    confirmAddMissingPlants() {
      if (this.missingPlantsSelected.length === 0) {
        this.$swal({
          type: 'warning',
          title: 'แจ้งเตือน',
          text: 'กรุณาเลือกชนิดไม้ที่ต้องการเพิ่มอย่างน้อย 1 รายการ'
        });
        return;
      }

      this.$swal({
        title: 'ยืนยันการเพิ่มชนิดไม้',
        text: `คุณต้องการเพิ่มชนิดไม้ ${this.missingPlantsSelected.length} รายการ ใช่หรือไม่?`,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.value) {
          this.addMissingPlants();
        }
      });
    },
    async addMissingPlants() {
      this.isLoading = true; // Use global loading or a specific one
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_IPFLASK}/addPlant`,
          { plants: this.missingPlantsSelected },
          { withCredentials: true }
        );

        if (response.data && response.data.plants) {
          this.missingPlantsDialog = false;
          this.$swal({
            type: 'success',
            title: 'เพิ่มชนิดไม้สำเร็จ',
            text: `เพิ่มชนิดไม้เรียบร้อยแล้ว ${response.data.plants.length} รายการ กรุณากดอัปโหลดไฟล์ใหม่อีกครั้ง`,
          });
          // Clear internal state
          this.missingPlantsList = [];
          this.missingPlantsSelected = [];
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Error adding plants:', error);
        const errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'ไม่สามารถเพิ่มชนิดไม้ได้';
        this.$swal({
          type: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: errorMessage
        });
      } finally {
        this.isLoading = false;
      }
    },

    deleteUser(user) {
      // Count users with the same card_id
      const usersWithSameCardId = this.parsedData.users.filter(
        u => u.card_id === user.card_id
      );

      // Count related plants
      const relatedPlants = this.parsedData.user_plants.filter(
        plant => plant.card_id === user.card_id
      );

      const relatedCount = relatedPlants.length;
      const hasOtherUsersWithSameCardId = usersWithSameCardId.length > 1;

      let message = `คุณแน่ใจหรือไม่ที่จะลบผู้ใช้ ${user.title}${user.name} ${user.surname}`;

      if (relatedCount > 0 && !hasOtherUsersWithSameCardId) {
        message += `\n\nการลบผู้ใช้รายนี้จะทำให้บันทึกการปลูกต้นไม้ของผู้ใช้อีก ${relatedCount} รายการถูกลบด้วย`;
      }

      this.$swal({
        title: 'ยืนยันการลบผู้ใช้',
        text: message,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'ลบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.value) {
          // Delete user ตาม localId
          const index = this.parsedData.users.findIndex(
            u => u._localId === user._localId
          );
          if (index !== -1) {
            this.parsedData.users.splice(index, 1);
          }

          // Delete related plants only if this is the last user with this card_id
          if (!hasOtherUsersWithSameCardId) {
            this.parsedData.user_plants = this.parsedData.user_plants.filter(
              plant => plant.card_id !== user.card_id
            );
          }

          this.$swal({
            type: 'success',
            title: 'ลบเรียบร้อย',
            text: hasOtherUsersWithSameCardId
              ? 'ลบผู้ใช้แล้ว'
              : `ลบผู้ใช้และบันทึกการปลูก ${relatedCount} รายการแล้ว`,
            timer: 1500
          });
        }
      });
    },

    deletePlant(plant) {
      this.$swal({
        title: 'ยืนยันการลบ',
        text: 'คุณแน่ใจหรือไม่ที่จะลบบันทึกการปลูกต้นไม้นี้',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'ลบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.value) {
          const index = this.parsedData.user_plants.findIndex(
            p => p._localId === plant._localId
          );
          if (index !== -1) {
            this.parsedData.user_plants.splice(index, 1);
          }
          this.$swal({
            type: 'success',
            title: 'ลบเรียบร้อย',
            timer: 1500
          });
        }
      });
    },

    /* Editing helpers for users */
    startEditingUser(user) {
      this.editingUserId = user._localId;
      this.editingUserBackup = JSON.parse(JSON.stringify(user));
    },

    saveUserEdit() {
      this.editingUserId = null;
      this.editingUserBackup = null;
    },

    cancelUserEdit() {
      if (this.editingUserId && this.editingUserBackup) {
        const index = this.parsedData.users.findIndex(
          u => u._localId === this.editingUserId
        );
        if (index !== -1) {
          this.$set(
            this.parsedData.users,
            index,
            JSON.parse(JSON.stringify(this.editingUserBackup))
          );
        }
      }
      this.editingUserId = null;
      this.editingUserBackup = null;
    },

    /* ---- PLANT EDIT DIALOG ---- */

    async openPlantEditDialog(plant) {
      this.plantEditTargetId = plant._localId;
      this.plantEditModel = JSON.parse(JSON.stringify(plant));

      // Ensure id fields exist on the model (if provided by parsed data)
      this.plantEditModel.plant_id = plant.plant_id || this.plantEditModel.plant_id || null;
      this.plantEditModel.province_id = plant.province_id || this.plantEditModel.province_id || null;
      this.plantEditModel.amphoe_id = plant.amphoe_id || this.plantEditModel.amphoe_id || null;
      this.plantEditModel.tambon_id = plant.tambon_id || this.plantEditModel.tambon_id || null;
      this.plantEditModel.sub_department_id_level1 = plant.sub_department_id_level1 || this.plantEditModel.sub_department_id_level1 || null;
      this.plantEditModel.sub_department_id_level2 = plant.sub_department_id_level2 || this.plantEditModel.sub_department_id_level2 || null;
      this.plantEditModel.sub_department_name_level2 = plant.sub_department_name_level2 || this.plantEditModel.sub_department_name_level2 || null;

      // preset ค่าค้นหา autocomplete ให้โชว์รายการเดิมก่อน
      this.plantSearch = this.plantEditModel.plant_name || '';
      this.provinceSearch = this.plantEditModel.province_name || '';
      this.amphoeSearch = this.plantEditModel.amphoe_name || '';
      this.tambonSearch = this.plantEditModel.tambon_name || '';
      this.subDepartmentSearch = this.plantEditModel.sub_department_name_level2 || '';

      // Preload options so the current values appear in the dropdowns and selections
      try {
        // plants
        const plants = await this.fetchWithCache(this.plantCache, 'all', '/queryPlant', {});
        this.plantOptions = plants || [];

        // provinces
        const provinces = await this.fetchWithCache(this.provinceCache, 'all', '/queryProvince', {});
        this.provinceOptions = provinces || [];

        // amphoes for selected province
        if (this.plantEditModel.province_id) {
          const provKey = `prov:${this.plantEditModel.province_id}`;
          const amphoes = await this.fetchWithCache(this.amphoeCache, provKey, '/queryAmphoe', { province_id: this.plantEditModel.province_id });
          this.amphoeOptions = amphoes || [];
        } else {
          this.amphoeOptions = [];
        }

        // tambons for selected amphoe
        if (this.plantEditModel.amphoe_id) {
          const ampKey = `amp:${this.plantEditModel.amphoe_id}`;
          const tambons = await this.fetchWithCache(this.tambonCache, ampKey, '/queryTambon', { amphoe_id: this.plantEditModel.amphoe_id });
          this.tambonOptions = tambons || [];
        } else {
          this.tambonOptions = [];
        }

        // sub-departments
        const subDepts = await this.fetchWithCache(this.subDepartmentCache, 'all', '/querySubSubDepartment', {});
        this.subDepartmentOptions = subDepts || [];
      } catch (e) {
        console.error('preload autocomplete options error', e);
      }

      this.plantEditDialog = true;
    },

    closePlantEditDialog() {
      this.plantEditDialog = false;
      this.plantEditModel = null;
      this.plantEditTargetId = null;
      this.plantSearch = '';
      this.provinceSearch = '';
      this.amphoeSearch = '';
      this.tambonSearch = '';
      this.subDepartmentSearch = '';
    },

    savePlantDialog() {
      if (!this.plantEditTargetId || !this.plantEditModel) {
        this.closePlantEditDialog();
        return;
      }

      const index = this.parsedData.user_plants.findIndex(
        p => p._localId === this.plantEditTargetId
      );
      if (index !== -1) {
        // เขียนค่าที่แก้กลับไปยังแถวจริง
        this.$set(this.parsedData.user_plants, index, {
          ...this.parsedData.user_plants[index],
          ...this.plantEditModel
        });
      }

      this.closePlantEditDialog();
    },

    /* ---- AUTOCOMPLETE + CACHE ---- */

    // Generic fetch with POST and caching. Returns array of objects { id, name, ... }
    async fetchWithCache(cacheMap, cacheKey, url, postData = {}) {
      if (!cacheKey) return [];

      if (cacheMap.has(cacheKey)) {
        return cacheMap.get(cacheKey);
      }

      const response = await this.axios.post(
        `${process.env.VUE_APP_IPFLASK}${url}`,
        postData
      );

      const data = response.data || [];

      const items = data.map(item => {
        let id = null;
        let name = null;

        if (url === '/queryPlant') {
          id = item.plant_id;
          name = item.plant_name;
        } else if (url === '/queryProvince') {
          id = item.province_id;
          name = item.province_name;
        } else if (url === '/queryAmphoe') {
          id = item.amphoe_id;
          name = item.amphoe_name;
        } else if (url === '/queryTambon') {
          id = item.tambon_id;
          name = item.tambon_name;
        } else if (url === '/querySubSubDepartment') {
          id = item.department_id;
          name = item.department_name;
        } else {
          // fallback สำหรับ endpoint อื่น ๆ ในอนาคต
          id = item.id || item.code || null;
          name = item.name || null;
        }

        return {
          id,
          name,
          raw: item
        };
      });

      cacheMap.set(cacheKey, items);
      return items;
    },

    async onSearchPlant(val) {
      const q = (val || '').trim();
      this.plantSearch = q;
      if (q.length < 2) {
        this.plantOptions = [];
        return;
      }
      this.plantLoading = true;
      try {
        // backend returns full list via POST (no body required). Cache under key 'all'
        const all = await this.fetchWithCache(this.plantCache, 'all', '/queryPlant', {});
        // filter client-side by name
        const filtered = all.filter(it => it.name && it.name.toLowerCase().includes(q.toLowerCase()));
        this.plantOptions = filtered;
      } catch (e) {
        console.error('queryPlant error', e);
      } finally {
        this.plantLoading = false;
      }
    },
    onSelectPlant(val) {
      // val is the selected name (because v-autocomplete uses item-value=name)
      this.plantEditModel.plant_name = val;
      // set plant_id if available in cache
      const all = this.plantCache.get('all') || [];
      const found = all.find(it => it.name === val);
      if (found && found.id) {
        this.plantEditModel.plant_id = found.id;
      }
    },

    async onSearchProvince(val) {
      const q = (val || '').trim();
      this.provinceSearch = q;
      if (q.length < 2) {
        this.provinceOptions = [];
        return;
      }
      this.provinceLoading = true;
      try {
        // backend returns full list via POST (no body). Cache under key 'all'
        const all = await this.fetchWithCache(this.provinceCache, 'all', '/queryProvince', {});
        const filtered = all.filter(it => it.name && it.name.toLowerCase().includes(q.toLowerCase()));
        this.provinceOptions = filtered;
      } catch (e) {
        console.error('queryProvince error', e);
      } finally {
        this.provinceLoading = false;
      }
    },
    async onSelectProvince(val) {
      this.plantEditModel.province_name = val;
      // set province_id if available
      const all = this.provinceCache.get('all') || [];
      const found = all.find(it => it.name === val);
      if (found && found.id) {
        this.plantEditModel.province_id = found.id;
      } else {
        this.plantEditModel.province_id = null;
      }

      // Clear dependent fields (both name + id) when province changes
      this.plantEditModel.amphoe_name = '';
      this.plantEditModel.amphoe_id = null;
      this.plantEditModel.tambon_name = '';
      this.plantEditModel.tambon_id = null;
      this.amphoeOptions = [];
      this.tambonOptions = [];
      // Load amphoe list for selected province immediately
      if (this.plantEditModel.province_id) {
        const provId = this.plantEditModel.province_id;
        const cacheKey = `prov:${provId}`;
        this.amphoeLoading = true;
        this.amphoeOptions = [];
        this.tambonOptions = [];
        try {
          const amphoes = await this.fetchWithCache(this.amphoeCache, cacheKey, '/queryAmphoe', { province_id: provId });
          this.amphoeOptions = amphoes || [];
        } catch (e) {
          console.error('load amphoe after province select error', e);
        } finally {
          this.amphoeLoading = false;
        }
      }
    },

    async onSearchAmphoe(val) {
      const q = (val || '').trim();
      this.amphoeSearch = q;
      // need province_id to query amphoe
      const provinceId = this.plantEditModel && this.plantEditModel.province_id;
      if (!provinceId) {
        this.amphoeOptions = [];
        return;
      }
      this.amphoeLoading = true;
      try {
        const cacheKey = `prov:${provinceId}`;
        const all = await this.fetchWithCache(this.amphoeCache, cacheKey, '/queryAmphoe', { province_id: provinceId });
        const filtered = all.filter(it => it.name && it.name.toLowerCase().includes(q.toLowerCase()));
        this.amphoeOptions = filtered;
      } catch (e) {
        console.error('queryAmphoe error', e);
      } finally {
        this.amphoeLoading = false;
      }
    },
    async onSelectAmphoe(val) {
      this.plantEditModel.amphoe_name = val;
      // set amphoe_id if available
      const provinceId = this.plantEditModel && this.plantEditModel.province_id;
      const provCacheKey = provinceId ? `prov:${provinceId}` : null;
      const amphoeList = provCacheKey ? (this.amphoeCache.get(provCacheKey) || []) : [];
      const found = amphoeList.find(it => it.name === val);
      if (found && found.id) {
        this.plantEditModel.amphoe_id = found.id;
      } else {
        this.plantEditModel.amphoe_id = null;
      }

      // Clear dependent tambon (both name + id) every time amphoe changes
      this.plantEditModel.tambon_name = '';
      this.plantEditModel.tambon_id = null;
      this.tambonOptions = [];
      if (this.plantEditModel.amphoe_id) {
        const amphoeId = this.plantEditModel.amphoe_id;
        const tambonCacheKey = `amp:${amphoeId}`;
        this.tambonLoading = true;
        try {
          const tambons = await this.fetchWithCache(this.tambonCache, tambonCacheKey, '/queryTambon', { amphoe_id: amphoeId });
          this.tambonOptions = tambons || [];
        } catch (e) {
          console.error('load tambon after amphoe select error', e);
        } finally {
          this.tambonLoading = false;
        }
      }
    },

    async onSearchTambon(val) {
      const q = (val || '').trim();
      this.tambonSearch = q;
      // need amphoe_id to query tambon
      const amphoeId = this.plantEditModel && this.plantEditModel.amphoe_id;
      if (!amphoeId) {
        this.tambonOptions = [];
        return;
      }
      this.tambonLoading = true;
      try {
        const cacheKey = `amp:${amphoeId}`;
        const all = await this.fetchWithCache(this.tambonCache, cacheKey, '/queryTambon', { amphoe_id: amphoeId });
        const filtered = all.filter(it => it.name && it.name.toLowerCase().includes(q.toLowerCase()));
        this.tambonOptions = filtered;
      } catch (e) {
        console.error('queryTambon error', e);
      } finally {
        this.tambonLoading = false;
      }
    },
    onSelectTambon(val) {
      // If cleared by user (val null/''), clear both name and id
      if (!val) {
        this.plantEditModel.tambon_name = '';
        this.plantEditModel.tambon_id = null;
        return;
      }

      this.plantEditModel.tambon_name = val;

      // set tambon_id and lat/lng if available
      const amphoeId = this.plantEditModel && this.plantEditModel.amphoe_id;
      const cacheKey = amphoeId ? `amp:${amphoeId}` : null;
      const list = cacheKey ? (this.tambonCache.get(cacheKey) || []) : [];
      const found = list.find(it => it.name === val);
      if (found && found.raw) {
        this.plantEditModel.tambon_id = found.id || found.raw.tambon_id || null;
        // copy latitude/longitude if present
        if (found.raw.latitude !== undefined) this.plantEditModel.latitude = found.raw.latitude;
        if (found.raw.longitude !== undefined) this.plantEditModel.longitude = found.raw.longitude;
      } else {
        // If not found, clear id to be safe
        this.plantEditModel.tambon_id = null;
      }
    },

    async onSearchSubDepartment(val) {
      const q = (val || '').trim();
      this.subDepartmentSearch = q;
      if (q.length < 2) {
        this.subDepartmentOptions = [];
        return;
      }
      this.subDepartmentLoading = true;
      try {
        // backend returns full list via POST (no body required). Cache under key 'all'
        const all = await this.fetchWithCache(this.subDepartmentCache, 'all', '/querySubSubDepartment', {});
        // filter client-side by name
        const filtered = all.filter(it => it.name && it.name.toLowerCase().includes(q.toLowerCase()));
        this.subDepartmentOptions = filtered;
      } catch (e) {
        console.error('querySubSubDepartment error', e);
      } finally {
        this.subDepartmentLoading = false;
      }
    },
    onSelectSubDepartment(val) {
      // If cleared, clear both name and ids
      if (!val) {
        this.plantEditModel.sub_department_name_level2 = '';
        this.plantEditModel.sub_department_id_level2 = null;
        this.plantEditModel.sub_department_id_level1 = null;
        return;
      }

      this.plantEditModel.sub_department_name_level2 = val;

      const all = this.subDepartmentCache.get('all') || [];
      const found = all.find(it => it.name === val);
      if (found) {
        if (found.id !== undefined && found.id !== null) {
          this.plantEditModel.sub_department_id_level2 = found.id;
        } else {
          this.plantEditModel.sub_department_id_level2 = null;
        }

        if (found.raw && (found.raw.parent_department_id !== undefined)) {
          this.plantEditModel.sub_department_id_level1 = found.raw.parent_department_id;
        } else {
          this.plantEditModel.sub_department_id_level1 = null;
        }
      } else {
        this.plantEditModel.sub_department_id_level2 = null;
        this.plantEditModel.sub_department_id_level1 = null;
      }
    },

    /* ---- IMPORT ---- */
    confirmImport() {
      this.$swal({
        title: 'ยืนยันการนำเข้าข้อมูล',
        html: `<p>คุณแน่ใจหรือไม่ที่จะนำเข้าข้อมูล</p>
               <p><strong>ผู้ใช้: ${this.parsedData.users.length} รายการ</strong></p>
               <p><strong>บันทึกการปลูก: ${this.parsedData.user_plants.length} รายการ</strong></p>`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'นำเข้า',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.value) {
          this.importData();
        }
      });
    },

    async importData() {
      this.isImporting = true;
      try {
        // ตัด _localId ออกก่อนส่งเข้า backend
        const usersForImport = this.parsedData.users.map(u => {
          const { _localId, ...rest } = u;
          return rest;
        });

        const plantsForImport = this.parsedData.user_plants.map(p => {
          const { _localId, ...rest } = p;
          return rest;
        });

        const payload = {
          users: usersForImport,
          user_plants: plantsForImport
        };

        const response = await this.axios.post(
          `${process.env.VUE_APP_IPFLASK}/importNurseryData`,
          payload,
          { withCredentials: true }
        );

        let swalText = (response.data && response.data.message) || 'นำเข้าข้อมูลสำเร็จแล้ว';
        let swalHtml = '';
        if (response.data && response.data.summary) {
          const summary = response.data.summary;
          swalHtml += `<p><strong>ผู้ใช้ที่นำเข้า: ${summary.users_inserted || 0} รายการ</strong></p>`;
          swalHtml += `<p><strong>บันทึกการปลูกที่นำเข้า: ${summary.user_plants_inserted || 0} รายการ</strong></p>`;
          if (Array.isArray(summary.errors) && summary.errors.length > 0) {
            swalHtml += `<hr><p style='color:red'><strong>พบข้อผิดพลาด ${summary.errors.length} รายการ:</strong></p>`;
            swalHtml += '<ul style="text-align:left;max-height:200px;overflow:auto">';
            summary.errors.slice(0, 10).forEach((err, idx) => {
              const errorMsg = err.error || 'Unknown error';
              const cardId = err.user_plant && err.user_plant.card_id ? err.user_plant.card_id : '';
              const plantName = err.user_plant && err.user_plant.plant_name ? err.user_plant.plant_name : '';
              swalHtml += `<li><strong>${errorMsg}</strong><br>บัตร: ${cardId} | ชนิดไม้: ${plantName}</li>`;
            });
            if (summary.errors.length > 10) {
              swalHtml += `<li>...แสดงเฉพาะ 10 รายการแรก...</li>`;
            }
            swalHtml += '</ul>';
          }
        }
        this.$swal({
          type: 'success',
          title: 'นำเข้าสำเร็จ',
          text: swalText,
          html: swalHtml || undefined
        }).then(() => {
          // Reset form
          this.fileName = '';
          this.selectedFile = null;
          this.parsedData = { users: [], user_plants: [] };
          this.userLocalIdCounter = 1;
          this.plantLocalIdCounter = 1;
          this.editingUserId = null;
          this.editingUserBackup = null;
          this.$refs.fileInput.value = '';
        });
      } catch (error) {
        console.error('Error importing data:', error);
        const errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'ไม่สามารถนำเข้าข้อมูลได้';
        this.$swal({
          type: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: errorMessage
        });
      } finally {
        this.isImporting = false;
      }
    }
  }
};
</script>

<style scoped>
.v-data-table {
  font-size: 0.85rem;
}

.v-card {
  border-radius: 4px;
}

.v-card-title {
  font-weight: 500;
  font-size: 1.1rem;
}

.action-btn {
  background: transparent !important;
  box-shadow: none !important;
  min-width: 32px;
  padding: 6px !important;
}
</style>
